import { useTranslation } from 'react-i18next'

import { DonationStatusTableCell } from '../DonationListTable/DonationStatusTableCell'
import { donationTypeTranslations } from '../DonationListTable/DonationListTable.types'
import defaultOrgLogo from '../../../../common/assets/images/placeholder-logo.svg'

import { Avatar, Drawer, Flex, Heading, Spacer, Text } from '@percent/lemonade'
import { CurrencyCode, formatMoney } from '@percent/utility'
import styles from './DonationDrawer.module.scss'
import { Donation } from '@percent/partner-dashboard/services/donation/donationService.types'
import { DonationStatusCard } from './DonationStatusCard/DonationStatusCard'
import { getFormattedDate } from '@percent/partner-dashboard/common/library/utility/date'
import { getDisplayDonationStatus } from '@percent/partner-dashboard/common/library/utility/getDisplayDonationStatus'

export function DonationDrawer({
  opened,
  onClose,
  onOpen,
  donation
}: {
  opened: boolean
  onClose: () => void
  onOpen: () => void
  donation: Donation
}) {
  const {
    t,
    i18n: { language: locale }
  } = useTranslation()

  const total = formatMoney(
    {
      amount: donation.amount,
      currency: donation.currencyCode as CurrencyCode
    },
    { locale }
  )

  const updatedDonationStatus = getDisplayDonationStatus(donation)

  return (
    <Drawer isOpen={opened} onOpen={onOpen} onClose={onClose} title={t('typography.donationStatus')} position="right">
      <Flex direction="row" gap="small" justify="space-between" align="center">
        <Heading level="h4" data-testid="donation-header-name">
          {donation.organisation?.data?.name}
        </Heading>
        <Avatar
          image={donation.organisation?.data?.logo || defaultOrgLogo}
          alt={donation.organisation?.data?.name || 'organisation logo'}
          transparent
        />
      </Flex>
      <Spacer size="s" axis="vertical" />
      <Heading level="h2" data-testid="total-amount">
        {total}
      </Heading>
      <Spacer size="xs" axis="vertical" />
      <Text size="small">{getFormattedDate(donation.createdAt)}</Text>
      <Spacer size="s" axis="vertical" />
      <Flex direction="row" gap="small" justify="space-between">
        <Text size="small">{t(donationTypeTranslations[donation.type])}</Text>
        <Heading level="h4">{total}</Heading>
      </Flex>
      <Spacer size="s" axis="vertical" />
      <Flex direction="row" gap="small" justify="space-between">
        <DonationStatusTableCell status={updatedDonationStatus} />
      </Flex>
      <Spacer size="xs" axis="vertical" />
      <DonationStatusCard donation={donation} />
      {donation.giftNote && (
        <>
          <Spacer size="xs" axis="vertical" />
          <Text size="small" fontWeight="semiBold">
            {t('typography.giftNote')}
          </Text>
          <Text as="p" size="small" className={styles.giftNote} dataTestId="donation-gift-note">
            {donation.giftNote}
          </Text>
          <Spacer size="s" axis="vertical" />
        </>
      )}
      <div className={styles.donationDrawerFooter}>
        <Flex direction="row" gap="small" justify="space-between">
          <Heading level="h4">{t('typography.totalDonated')}</Heading>
          <Heading level="h4">{total}</Heading>
        </Flex>
      </div>
    </Drawer>
  )
}
